import { useEffect, useRef, useState } from "react";
import { loadMapLibre } from "./mapLoader";
import { applyMapLanguage } from "./mapLabels";
import { ATTRIBUTION, DEFAULT_CENTER, MAP_STYLES } from "./mapConfig";
import { circleGeoJson } from "./geoCircle";

const COLORS = { branch: "#22c55e", duty: "#f59e0b", person: "#3b82f6", late: "#ef4444", out: "#94a3b8" };

const circles = (items) => ({
  type: "FeatureCollection",
  features: items.map((item) => circleGeoJson(item.latitude, item.longitude, item.radius_m || 50)),
});

const dot = (gl, color, title) => {
  const element = document.createElement("div");
  element.title = title || "";
  element.style.cssText = `width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 3px ${color}44`;
  return new gl.Marker({ element });
};

// خريطة الحضور المباشر: نطاقات الفروع + مواقع الواجبات + نقاط تسجيل الموظفين اليوم.
export const useLiveMap = ({ container, data, lang, theme }) => {
  const map = useRef(null);
  const gl = useRef(null);
  const markers = useRef([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadMapLibre()
      .then((lib) => {
        if (cancelled || !container.current || map.current) return;
        gl.current = lib;
        map.current = new lib.Map({
          container: container.current,
          style: MAP_STYLES[theme] || MAP_STYLES.light,
          center: [DEFAULT_CENTER.longitude, DEFAULT_CENTER.latitude],
          zoom: 11,
          attributionControl: { compact: true, customAttribution: ATTRIBUTION },
        });
        map.current.addControl(new lib.NavigationControl({ showCompass: false }), "top-left");
        map.current.on("style.load", () => {
          applyMapLanguage(map.current, lang);
          ["branches", "duties"].forEach((key) => {
            if (map.current.getSource(key)) return;
            const color = key === "branches" ? COLORS.branch : COLORS.duty;
            map.current.addSource(key, { type: "geojson", data: circles([]) });
            map.current.addLayer({ id: `${key}-fill`, type: "fill", source: key, paint: { "fill-color": color, "fill-opacity": 0.14 } });
            map.current.addLayer({ id: `${key}-line`, type: "line", source: key, paint: { "line-color": color, "line-width": 2 } });
          });
        });
      })
      .catch((err) => setError(err.message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map.current || !gl.current || !data) return;
    const draw = () => {
      ["branches", "duties"].forEach((key) => {
        const source = map.current.getSource(key);
        if (source) source.setData(circles(data[key] || []));
      });
      markers.current.forEach((marker) => marker.remove());
      markers.current = [];
      const points = [];
      (data.duties || []).forEach((duty) => {
        markers.current.push(dot(gl.current, COLORS.duty, duty.title).setLngLat([duty.longitude, duty.latitude]).addTo(map.current));
        points.push([duty.longitude, duty.latitude]);
      });
      (data.people || []).forEach((person) => {
        const spot = person.check_in || person.check_out;
        if (!spot) return;
        const color = person.check_out_at ? COLORS.out : person.late_minutes > 0 ? COLORS.late : COLORS.person;
        markers.current.push(dot(gl.current, color, person.name).setLngLat([spot.longitude, spot.latitude]).addTo(map.current));
        points.push([spot.longitude, spot.latitude]);
      });
      (data.branches || []).forEach((branch) => points.push([branch.longitude, branch.latitude]));
      if (points.length) {
        const bounds = points.reduce((box, point) => box.extend(point), new gl.current.LngLatBounds(points[0], points[0]));
        map.current.fitBounds(bounds, { padding: 70, maxZoom: 15, duration: 600 });
      }
    };
    if (map.current.isStyleLoaded()) draw();
    else map.current.once("idle", draw);
  }, [data]);

  const focus = (longitude, latitude) =>
    map.current && map.current.flyTo({ center: [longitude, latitude], zoom: 16, duration: 700 });

  return { error, focus };
};
